import { useState, useEffect } from "react";
import { X, User, Search, Upload, Image as ImageIcon, Plus, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { FORMAS_PAGAMENTO } from "@/components/financeiro/FinanceiroForm";

interface Cliente {
  id: string;
  nome: string;
}

interface Tatuador {
  id: string;
  tatuador_id: string;
  usuarios?: { nome: string };
}

interface AgendamentoFormProps {
  initialData?: any;
  clientes: Cliente[];
  equipe: Tatuador[];
  isDono: boolean;
  currentUserId: string;
  currentUserNome: string;
  effectiveStudioId: string;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  title: string;
}

export function AgendamentoForm({ initialData, clientes, equipe, isDono, currentUserId, currentUserNome, effectiveStudioId, onSubmit, onCancel, title }: AgendamentoFormProps) {
  const [tipo, setTipo] = useState<"cliente" | "pessoal">(initialData?.tipo || "cliente");
  const [searchCliente, setSearchCliente] = useState("");
  const [showClienteList, setShowClienteList] = useState(false);
  const [isAddingNewCliente, setIsAddingNewCliente] = useState(false);
  const [isSavingCliente, setIsSavingCliente] = useState(false);
  const [newClienteData, setNewClienteData] = useState({
    nome: "",
    telefone: "",
    email: ""
  });
  
  const [formData, setFormData] = useState({
    tatuador_id: initialData?.tatuador_id || "",
    cliente_id: initialData?.cliente_id || "",
    cliente_nome: initialData?.cliente_nome || "",
    titulo: initialData?.titulo || "",
    data: initialData?.data || new Date().toISOString().split('T')[0],
    horario_inicio: initialData?.horario_inicio || "09:00",
    horario_fim: initialData?.horario_fim || "10:00",
    duracao: initialData?.duracao || "2",
    valor_total: initialData?.valor_total || "",
    valor_sinal: initialData?.valor_sinal || "",
    forma_pagamento: initialData?.forma_pagamento || "",
    parcelas: initialData?.parcelas || "",
    regiao_corpo: initialData?.regiao_corpo || "",
    estilo: initialData?.estilo || "Blackwork",
    observacoes: initialData?.observacoes || "",
    status: initialData?.status || "Confirmado",
    imagem_referencia: initialData?.imagem_referencia || null
  });

  const filteredClientes = clientes.filter(c => 
    c.nome.toLowerCase().includes(searchCliente.toLowerCase())
  );

  const handleAddNewCliente = async () => {
    if (!newClienteData.nome) {
      toast.error("O nome do cliente é obrigatório");
      return;
    }

    setIsSavingCliente(true);
    try {
      if (!effectiveStudioId) {
        toast.error("Usuário não autenticado");
        return;
      }

      const { data, error } = await supabase
        .from("clientes")
        .insert({
          nome: newClienteData.nome,
          telefone: newClienteData.telefone,
          email: newClienteData.email,
          studio_id: effectiveStudioId,
          created_by: currentUserId || null,
          status: "Ativo"
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        handleChange("cliente_id", data.id);
        handleChange("cliente_nome", data.nome);
        setIsAddingNewCliente(false);
        setSearchCliente("");
        setShowClienteList(false);
        setNewClienteData({ nome: "", telefone: "", email: "" });
        toast.success("Cliente cadastrado e selecionado!");
      }
    } catch (error: any) {
      console.error("Erro ao cadastrar cliente:", error);
      toast.error("Erro ao cadastrar cliente: " + error.message);
    } finally {
      setIsSavingCliente(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (tipo === "cliente") {
      if (!formData.cliente_id || !formData.data || !formData.horario_inicio || !formData.valor_total) {
        toast.error("Preencha todos os campos obrigatórios!");
        return;
      }
    } else {
      if (!formData.titulo || !formData.data || !formData.horario_inicio) {
        toast.error("Preencha todos os campos obrigatórios!");
        return;
      }
    }

    const clean = Object.fromEntries(
      Object.entries({ ...formData, tipo }).map(([k, v]) => [k, v === "" ? null : v])
    );
    onSubmit(clean);
  };

  const estilos = [
    "Blackwork", "Realismo", "Fineline", "Aquarela", 
    "Neotradicional", "Tribal", "Geométrico", "Old School", "Outros"
  ];

  return (
    <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full max-w-2xl mx-auto shadow-sm max-h-[90vh] overflow-y-auto custom-scrollbar animate-in zoom-in-95 duration-200">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-muted-foreground hover:text-foreground hover:bg-muted">
          <X size={24} />
        </Button>
      </div>

      <div className="flex items-center justify-center gap-4 mb-8 bg-background p-1 rounded-xl border border-border w-fit mx-auto">
        <button
          onClick={() => setTipo("cliente")}
          className={cn(
            "px-6 py-2 rounded-lg text-sm font-semibold transition-all",
            tipo === "cliente" ? "bg-primary text-foreground shadow-lg" : "text-muted-foreground hover:text-white"
          )}
        >
          Agendamento de Cliente
        </button>
        <button
          onClick={() => setTipo("pessoal")}
          className={cn(
            "px-6 py-2 rounded-lg text-sm font-semibold transition-all",
            tipo === "pessoal" ? "bg-primary text-foreground shadow-lg" : "text-muted-foreground hover:text-white"
          )}
        >
          Compromisso Pessoal
        </button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {tipo === "cliente" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 relative">
                <Label className="text-muted-foreground text-sm font-medium">Cliente *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input 
                    value={formData.cliente_nome || searchCliente}
                    onChange={(e) => {
                      setSearchCliente(e.target.value);
                      setShowClienteList(true);
                      if (formData.cliente_nome) handleChange("cliente_nome", "");
                    }}
                    onFocus={() => setShowClienteList(true)}
                    placeholder="Buscar cliente..." 
                    className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 pl-10 text-foreground"
                  />
                </div>
                {showClienteList && searchCliente && !isAddingNewCliente && (
                  <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {filteredClientes.length > 0 ? (
                      <>
                        {filteredClientes.map(cliente => (
                          <button
                            key={cliente.id}
                            type="button"
                            className="w-full px-4 py-3 text-left hover:bg-muted/50 hover:text-primary transition-colors border-b border-border last:border-0 flex items-center gap-2 text-foreground"
                            onClick={() => {
                              handleChange("cliente_id", cliente.id);
                              handleChange("cliente_nome", cliente.nome);
                              setSearchCliente("");
                              setShowClienteList(false);
                            }}
                          >
                            <User size={14} className="text-muted-foreground" />
                            {cliente.nome}
                          </button>
                        ))}
                        <button
                          type="button"
                          className="w-full px-4 py-3 text-left hover:bg-muted/50 text-primary transition-colors border-t border-border flex items-center gap-2 font-semibold text-sm"
                          onClick={() => {
                            setNewClienteData({ ...newClienteData, nome: searchCliente });
                            setIsAddingNewCliente(true);
                          }}
                        >
                          <Plus size={14} />
                          + Cadastrar "{searchCliente}" como novo cliente
                        </button>
                      </>
                    ) : (
                      <div className="p-1">
                        <div className="px-4 py-3 text-muted-foreground text-sm italic">Nenhum cliente encontrado</div>
                        <button
                          type="button"
                          className="w-full px-4 py-3 text-left hover:bg-muted/50 text-primary transition-colors flex items-center gap-2 font-semibold text-sm"
                          onClick={() => {
                            setNewClienteData({ ...newClienteData, nome: searchCliente });
                            setIsAddingNewCliente(true);
                          }}
                        >
                          <Plus size={14} />
                          + Cadastrar novo cliente
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {isAddingNewCliente && (
                  <div className="mt-4 p-4 bg-background border border-border rounded-xl space-y-4 animate-in slide-in-from-top-2 duration-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-medium uppercase tracking-widest text-primary">Novo Cliente</h4>
                      <button 
                        type="button" 
                        onClick={() => setIsAddingNewCliente(false)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-[11px] text-muted-foreground uppercase font-normal">Nome Completo *</Label>
                        <Input 
                          value={newClienteData.nome}
                          onChange={(e) => setNewClienteData({...newClienteData, nome: e.target.value})}
                          placeholder="Nome do cliente"
                          className="bg-card border-border h-10 text-sm text-foreground"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-[11px] text-muted-foreground uppercase font-normal">Telefone</Label>
                          <Input 
                            value={newClienteData.telefone}
                            onChange={(e) => setNewClienteData({...newClienteData, telefone: e.target.value})}
                            placeholder="(00) 00000-0000"
                            className="bg-card border-border h-10 text-sm text-foreground"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-[11px] text-muted-foreground uppercase font-normal">E-mail</Label>
                          <Input 
                            value={newClienteData.email}
                            onChange={(e) => setNewClienteData({...newClienteData, email: e.target.value})}
                            placeholder="email@exemplo.com"
                            className="bg-card border-border h-10 text-sm text-foreground"
                          />
                        </div>
                      </div>
                      <Button 
                        type="button"
                        onClick={handleAddNewCliente}
                        disabled={isSavingCliente}
                        className="w-full bg-primary hover:bg-primary/90 text-white font-semibold h-10 text-sm mt-2 shadow-sm"
                      >
                        {isSavingCliente ? (
                          <Loader2 className="mr-2 size-4 animate-spin" />
                        ) : (
                          <Check className="mr-2 size-4" />
                        )}
                        Salvar e Selecionar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {isDono && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm font-medium">Tatuador</Label>
                  <Select value={formData.tatuador_id || ""} onValueChange={(v) => handleChange("tatuador_id", v)}>
                    <SelectTrigger className="bg-background border-border h-11 text-foreground">
                      <SelectValue placeholder="Selecionar tatuador (padrão: dono)" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border text-foreground">
                      {currentUserId && (
                        <SelectItem value={currentUserId}>
                          {currentUserNome || "Dono"}
                        </SelectItem>
                      )}
                      {equipe.map(t => (
                        <SelectItem key={t.id} value={t.tatuador_id}>
                          {t.usuarios?.nome || "Tatuador"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Data *</Label>
                <DatePicker
                  value={formData.data}
                  onChange={(v) => handleChange("data", v)}
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Horário de Início *</Label>
                <Input 
                  type="time"
                  value={formData.horario_inicio}
                  onChange={(e) => handleChange("horario_inicio", e.target.value)}
                  className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 [color-scheme:light] text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Duração Estimada (Horas)</Label>
                <Input 
                  type="number"
                  value={formData.duracao}
                  onChange={(e) => handleChange("duracao", e.target.value)}
                  placeholder="2"
                  className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Valor Total Cobrado *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
                  <Input 
                    type="number"
                    value={formData.valor_total}
                    onChange={(e) => handleChange("valor_total", e.target.value)}
                    placeholder="0,00"
                    className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 pl-10 text-foreground"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Valor do Sinal Pago *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
                  <Input 
                    type="number"
                    value={formData.valor_sinal}
                    onChange={(e) => handleChange("valor_sinal", e.target.value)}
                    placeholder="0,00"
                    className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 pl-10 text-foreground"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Forma de Pagamento</Label>
                <Select value={formData.forma_pagamento || ""} onValueChange={(v) => handleChange("forma_pagamento", v)}>
                  <SelectTrigger className="bg-background border-border h-11 text-foreground">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    {FORMAS_PAGAMENTO.map(fp => (
                      <SelectItem key={fp.value} value={fp.value} className="focus:bg-primary focus:text-white">
                        {fp.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.forma_pagamento === "credito" && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm font-medium">Parcelas</Label>
                  <Input
                    type="number"
                    min={1}
                    value={formData.parcelas}
                    onChange={(e) => handleChange("parcelas", e.target.value)}
                    placeholder="Ex: 3"
                    className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 text-foreground"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Região do Corpo</Label>
                <Input 
                  value={formData.regiao_corpo}
                  onChange={(e) => handleChange("regiao_corpo", e.target.value)}
                  placeholder="Ex: Braço, Costas"
                  className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Estilo da Tatuagem</Label>
                <Select value={formData.estilo} onValueChange={(v) => handleChange("estilo", v)}>
                  <SelectTrigger className="bg-background border-border focus:ring-primary h-11 text-foreground">
                    <SelectValue placeholder="Selecione o estilo" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    {estilos.map(estilo => (
                      <SelectItem key={estilo} value={estilo} className="focus:bg-primary focus:text-white">
                        {estilo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm font-medium">Status</Label>
              <div className="flex flex-wrap gap-2">
                {["Confirmado", "Reagendado", "Cancelado", "Concluído"].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleChange("status", s)}
                    className={cn(
                      "px-4 py-2 rounded-full text-xs font-semibold border transition-all",
                      formData.status === s 
                        ? "bg-primary border-primary text-white shadow-sm" 
                        : "bg-transparent border-border text-muted-foreground hover:border-muted-foreground"
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm font-medium">Referência</Label>
              <div className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center gap-2 hover:border-primary/50 transition-colors cursor-pointer group bg-background">
                <div className="size-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground group-hover:text-primary transition-colors">
                  <Upload size={24} />
                </div>
                <p className="text-sm font-medium text-muted-foreground">Clique para fazer upload ou arraste uma imagem</p>
                <p className="text-xs text-muted-foreground/60">PNG, JPG até 5MB</p>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm font-medium">Título do Compromisso *</Label>
              <Input 
                value={formData.titulo}
                onChange={(e) => handleChange("titulo", e.target.value)}
                placeholder="Ex: Almoço, Dentista, Workshop"
                className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 text-foreground"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Data *</Label>
                <DatePicker
                  value={formData.data}
                  onChange={(v) => handleChange("data", v)}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Início *</Label>
                <Input 
                  type="time"
                  value={formData.horario_inicio}
                  onChange={(e) => handleChange("horario_inicio", e.target.value)}
                  className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 [color-scheme:light] text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm font-medium">Fim</Label>
                <Input 
                  type="time"
                  value={formData.horario_fim}
                  onChange={(e) => handleChange("horario_fim", e.target.value)}
                  className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary h-11 [color-scheme:light] text-foreground"
                />
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-muted-foreground text-sm font-medium">Observações</Label>
          <Textarea 
            value={formData.observacoes}
            onChange={(e) => handleChange("observacoes", e.target.value)}
            placeholder="Alguma informação adicional..."
            className="bg-background border-border focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary min-h-[100px] resize-none text-foreground"
          />
        </div>

        <div className="flex gap-3 pt-4 sticky bottom-0 bg-card pb-2">
          <Button 
            type="submit" 
            className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold h-12 transition-all shadow-sm"
          >
            Salvar {tipo === "cliente" ? "Agendamento" : "Compromisso"}
          </Button>
          <Button 
            type="button" 
            variant="outline"
            onClick={onCancel}
            className="px-8 border-border bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground h-12 transition-all"
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}
